// src/components/AttachmentController.jsx
// ✅ SHARED ATTACHMENT CONTROLLER
// - Fast Chat / Document Intelligence:
//   select -> validate locally -> hold ORIGINAL file in browser memory -> upload ONLY when Send triggers it
//   Send-time upload: /chat/attachment/reserve -> signed PUT original to GCS -> /chat/attachment/commit
// - Vision Assist:
//   manual attachment access is intentionally DISABLED in this shared controller.
//   The legacy Vision upload implementation remains in this file for future reuse,
//   but Vision Assist cannot render/reach the hidden file input and the handler
//   defensively no-ops if invoked.
//   Screen-share frame upload/analyze is separate and is NOT affected here.
// - Fast Chat source files are never downscaled or converted in this controller
// - Fast Chat frontend size guard is 100MB; authoritative page counting/truncation belongs to the worker
// - Firebase ID token is obtained only when a backend operation is actually triggered
// - Main's safe documentIntelligence projection is preserved through polling and returned to ChatInterface
// - File Vault handoffs can reuse the exact same /commit + authoritative /status polling lifecycle
//   through the exported continueFastChatDocumentIntelligence() helper; no second progress system
// - ChatInterface owns one stable logical Send turnId; this controller only transports it through
//   /chat/attachment/reserve and /chat/attachment/commit for Fast Chat / Document Intelligence
// - Fast Chat / Document Intelligence percentage is backend-authoritative ONLY:
//   visible progressPercent comes from Main's document/status projection; browser reserve/upload/commit
//   phases never invent percentages
// - Real backend progressStage/progressPercent/progressMessage are preserved through polling
// - Backend denial/rejection/failure codes are preserved for ChatInterface so billing-specific
//   user messaging can be mapped there without matching fragile human-readable strings
// - Browser-native alert dialogs are intentionally not used; local/legacy attachment notices render
//   as centered in-app dialogs, while send-time Fast Chat errors remain structured for ChatInterface
// - Headless controller also supplies small file display metadata for ChatInput styling

import React, { useEffect, useRef, useState } from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";

const FAST_CHAT_MAX_FILE_SIZE_MB = 100;

// Vision Assist still uses the legacy attachment pipeline. Keep its existing
// guard isolated here so it cannot accidentally cap Fast Chat documents.
const VISION_LEGACY_MAX_FILE_SIZE_MB = 25;

const FAST_CHAT_SESSION_KINDS = new Set(["chat", "fast_chat", "fastchat"]);
const VISION_ASSIST_SESSION_KINDS = new Set(["vision_assist"]);
const FAST_CHAT_TERMINAL_STATUSES = new Set([
  "ready",
  "ready_with_warnings",
  "rejected",
  "failed",
]);
const FAST_CHAT_SUCCESS_STATUSES = new Set(["ready", "ready_with_warnings"]);

const FAST_CHAT_STATUS_POLL_INTERVAL_MS = 5000;
const FAST_CHAT_STATUS_POLL_MAX_ATTEMPTS = 660;

const ALLOWED_FILE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/jpg",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/plain",
  "text/csv",
];

// 🔧 Resize high-resolution images from camera/scan (frontend-only optimization)
const downscaleImage = async (file) => {
  return new Promise((resolve) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      const MAX_DIM = 1200;
      let { width, height } = img;

      if (width > MAX_DIM || height > MAX_DIM) {
        if (width > height) {
          height *= MAX_DIM / width;
          width = MAX_DIM;
        } else {
          width *= MAX_DIM / height;
          height = MAX_DIM;
        }
      } else {
        URL.revokeObjectURL(objectUrl);
        resolve(null);
        return;
      }

      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.floor(width));
      canvas.height = Math.max(1, Math.floor(height));
      const ctx = canvas.getContext("2d");

      try {
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      } catch {
        URL.revokeObjectURL(objectUrl);
        resolve(null);
        return;
      }

      canvas.toBlob(
        (blob) => {
          URL.revokeObjectURL(objectUrl);
          if (!blob) {
            resolve(null);
            return;
          }
          const resizedFile = new File([blob], file.name, { type: "image/jpeg" });
          resolve(resizedFile);
        },
        "image/jpeg",
        0.8
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(null);
    };

    img.src = objectUrl;
  });
};

// Create a safe preview (image only). Non-images return null (parent shows FileIcon)
const createSafePreview = async (file) => {
  if (!file?.type?.startsWith("image/")) return null;

  const img = new Image();
  const objectUrl = URL.createObjectURL(file);

  return new Promise((resolve) => {
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const MAX_DIM = 600;
      let { width, height } = img;

      if (width > height) {
        if (width > MAX_DIM) {
          height *= MAX_DIM / width;
          width = MAX_DIM;
        }
      } else {
        if (height > MAX_DIM) {
          width *= MAX_DIM / height;
          height = MAX_DIM;
        }
      }

      canvas.width = Math.max(1, Math.floor(width));
      canvas.height = Math.max(1, Math.floor(height));

      const ctx = canvas.getContext("2d");
      try {
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      } catch {
        URL.revokeObjectURL(objectUrl);
        resolve(null);
        return;
      }

      canvas.toBlob(
        (blob) => {
          URL.revokeObjectURL(objectUrl);
          if (!blob) {
            resolve(null);
            return;
          }
          resolve(URL.createObjectURL(blob));
        },
        "image/jpeg",
        0.8
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(null);
    };

    img.src = objectUrl;
  });
};

function getBackendBase() {
  const raw = process.env.REACT_APP_BACKEND_URL || "";
  return raw.replace(/\/+$/, "");
}

// ✅ Wait briefly for Firebase auth restore to finish (reduces "Missing or invalid token" on fast clicks)
async function getFirebaseIdToken() {
  const auth = getAuth();

  const getUserOrWait = async () => {
    if (auth.currentUser) return auth.currentUser;

    return await new Promise((resolve, reject) => {
      let unsub = null;
      const timer = setTimeout(() => {
        try {
          unsub?.();
        } catch {}
        reject(new Error("Missing or invalid token"));
      }, 3000);

      unsub = onAuthStateChanged(auth, (user) => {
        if (user) {
          clearTimeout(timer);
          try {
            unsub?.();
          } catch {}
          resolve(user);
        }
      });
    });
  };

  const user = await getUserOrWait();
  const token = await user.getIdToken(false);
  if (!token) throw new Error("Missing or invalid token");
  return token;
}

function normalizeSessionKind(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
}

function isFastChatSession(sessionKind) {
  return FAST_CHAT_SESSION_KINDS.has(normalizeSessionKind(sessionKind));
}

function isVisionAssistSession(sessionKind) {
  return VISION_ASSIST_SESSION_KINDS.has(normalizeSessionKind(sessionKind));
}

function isAllowedFileType(file) {
  if (!file) return false;

  return (
    ALLOWED_FILE_TYPES.includes(file.type) ||
    /\.(pdf|docx?|xlsx?|csv|txt|jpe?g|png|webp)$/i.test(file.name || "")
  );
}

function normalizedContentType(file) {
  const browserType = String(file?.type || "").trim().toLowerCase();
  if (browserType) return browserType;

  const name = String(file?.name || "").toLowerCase();
  if (/\.pdf$/.test(name)) return "application/pdf";
  if (/\.doc$/.test(name)) return "application/msword";
  if (/\.docx$/.test(name)) {
    return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
  }
  if (/\.xls$/.test(name)) return "application/vnd.ms-excel";
  if (/\.xlsx$/.test(name)) {
    return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
  }
  if (/\.csv$/.test(name)) return "text/csv";
  if (/\.txt$/.test(name)) return "text/plain";
  if (/\.jpe?g$/.test(name)) return "image/jpeg";
  if (/\.png$/.test(name)) return "image/png";
  if (/\.webp$/.test(name)) return "image/webp";

  return "application/octet-stream";
}

function fileExtensionFromName(name) {
  const raw = String(name || "").trim();
  const match = raw.match(/\.([A-Za-z0-9]+)$/);
  return match ? match[1].toLowerCase() : "";
}

function fileTypeLabel(fileOrName, mimeValue = "") {
  const name =
    typeof fileOrName === "string"
      ? fileOrName
      : String(fileOrName?.name || "");
  const mime = String(
    mimeValue ||
      (typeof fileOrName === "object" ? fileOrName?.type : "") ||
      ""
  )
    .trim()
    .toLowerCase();

  const ext = fileExtensionFromName(name);

  if (ext === "pdf" || mime === "application/pdf") return "PDF";
  if (ext === "doc") return "DOC";
  if (ext === "docx") return "DOCX";
  if (ext === "xls") return "XLS";
  if (ext === "xlsx") return "XLSX";
  if (ext === "csv") return "CSV";
  if (ext === "txt") return "TXT";
  if (ext === "jpg" || ext === "jpeg" || mime === "image/jpeg") return "JPEG";
  if (ext === "png" || mime === "image/png") return "PNG";
  if (ext === "webp" || mime === "image/webp") return "WEBP";

  return ext ? ext.toUpperCase() : "FILE";
}

function cleanErrorCode(value) {
  const normalized = String(value || "").trim();
  return normalized || null;
}

function extractBackendErrorCode(data) {
  if (!data || typeof data !== "object") return null;

  const doc =
    data?.document && typeof data.document === "object"
      ? data.document
      : {};
  const access =
    data?.documentIntelligenceAccess &&
    typeof data.documentIntelligenceAccess === "object"
      ? data.documentIntelligenceAccess
      : data?.document_intelligence_access &&
        typeof data.document_intelligence_access === "object"
      ? data.document_intelligence_access
      : {};

  return (
    cleanErrorCode(data?.code) ||
    cleanErrorCode(data?.reason) ||
    cleanErrorCode(data?.rejection_code) ||
    cleanErrorCode(data?.rejectionCode) ||
    cleanErrorCode(data?.failure_code) ||
    cleanErrorCode(data?.failureCode) ||
    cleanErrorCode(doc?.rejection_code) ||
    cleanErrorCode(doc?.rejectionCode) ||
    cleanErrorCode(doc?.failure_code) ||
    cleanErrorCode(doc?.failureCode) ||
    cleanErrorCode(access?.reason) ||
    cleanErrorCode(access?.code) ||
    null
  );
}

function createAttachmentError(
  message,
  {
    status = null,
    code = null,
    payload = null,
    documentId = null,
    documentStatus = null,
  } = {}
) {
  const safePayload =
    payload && typeof payload === "object" ? payload : null;
  const doc =
    safePayload?.document && typeof safePayload.document === "object"
      ? safePayload.document
      : {};
  const access =
    safePayload?.documentIntelligenceAccess &&
    typeof safePayload.documentIntelligenceAccess === "object"
      ? safePayload.documentIntelligenceAccess
      : safePayload?.document_intelligence_access &&
        typeof safePayload.document_intelligence_access === "object"
      ? safePayload.document_intelligence_access
      : null;

  const error = new Error(
    String(message || "Attachment request failed.")
  );
  error.name = "AttachmentError";
  error.status =
    Number.isFinite(Number(status)) && Number(status) > 0
      ? Number(status)
      : null;
  error.code =
    cleanErrorCode(code) || extractBackendErrorCode(safePayload);
  error.payload = safePayload;
  error.documentId =
    documentId ||
    safePayload?.documentId ||
    safePayload?.document_id ||
    doc?.document_id ||
    null;
  error.documentStatus =
    cleanErrorCode(documentStatus) ||
    cleanErrorCode(doc?.status) ||
    null;
  error.documentIntelligenceAccess = access;
  error.planType =
    cleanErrorCode(safePayload?.plan_type) ||
    cleanErrorCode(safePayload?.planType) ||
    cleanErrorCode(access?.plan_type) ||
    cleanErrorCode(access?.planType) ||
    null;
  error.rejectionCode =
    cleanErrorCode(safePayload?.rejection_code) ||
    cleanErrorCode(safePayload?.rejectionCode) ||
    cleanErrorCode(doc?.rejection_code) ||
    cleanErrorCode(doc?.rejectionCode) ||
    null;
  error.failureCode =
    cleanErrorCode(safePayload?.failure_code) ||
    cleanErrorCode(safePayload?.failureCode) ||
    cleanErrorCode(doc?.failure_code) ||
    cleanErrorCode(doc?.failureCode) ||
    null;

  return error;
}

async function readJsonResponse(res, fallbackMessage) {
  const raw = await res.text();
  let data = null;

  try {
    data = raw ? JSON.parse(raw) : {};
  } catch {
    throw createAttachmentError(
      res.status === 401
        ? "Missing or invalid token"
        : fallbackMessage || "Invalid server response",
      {
        status: res.status,
        code:
          res.status === 401
            ? "authentication_required"
            : "invalid_server_response",
      }
    );
  }

  if (!res.ok) {
    const message =
      data?.message ||
      data?.error ||
      data?.rejection_message ||
      data?.failure_message ||
      data?.document?.rejection_message ||
      data?.document?.failure_message ||
      fallbackMessage ||
      `Request failed (${res.status})`;

    throw createAttachmentError(message, {
      status: res.status,
      code: extractBackendErrorCode(data),
      payload: data,
    });
  }

  return data || {};
}

function AttachmentNoticeDialog({ notice, onClose }) {
  if (!notice) return null;

  return (
    <div
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose?.();
      }}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 10000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px",
        background: "rgba(15, 23, 42, 0.46)",
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="attachment-notice-title"
        style={{
          width: "min(440px, 100%)",
          borderRadius: "18px",
          background: "#ffffff",
          boxShadow: "0 24px 70px rgba(15, 23, 42, 0.28)",
          padding: "26px",
          color: "#0f172a",
        }}
      >
        <div
          id="attachment-notice-title"
          style={{
            fontSize: "19px",
            lineHeight: 1.3,
            fontWeight: 700,
            marginBottom: "10px",
          }}
        >
          {notice.title || "Attachment notice"}
        </div>

        <div
          style={{
            fontSize: "15px",
            lineHeight: 1.6,
            color: "#475569",
            whiteSpace: "pre-wrap",
          }}
        >
          {notice.message || "Please try again."}
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginTop: "22px",
          }}
        >
          <button
            type="button"
            onClick={onClose}
            autoFocus
            style={{
              border: 0,
              borderRadius: "10px",
              padding: "10px 18px",
              background: "#0f172a",
              color: "#ffffff",
              fontSize: "14px",
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
}

function documentMetaFromResult(result, fallback = {}) {
  const doc =
    result?.document && typeof result.document === "object"
      ? result.document
      : {};
  const dispatch =
    result?.dispatch && typeof result.dispatch === "object"
      ? result.dispatch
      : {};
  const meta =
    result?.meta && typeof result.meta === "object"
      ? result.meta
      : {};

  const documentId =
    result?.documentId ||
    result?.document_id ||
    doc?.document_id ||
    fallback?.documentId ||
    null;

  const documentStatus = String(
    doc?.status ||
      dispatch?.document_status ||
      fallback?.documentStatus ||
      ""
  )
    .trim()
    .toLowerCase();

  const pageCountRaw =
    doc?.actual_page_count ??
    meta?.page_count ??
    fallback?.pageCount ??
    null;

  const pageCount =
    Number.isFinite(Number(pageCountRaw)) && Number(pageCountRaw) > 0
      ? Number(pageCountRaw)
      : null;

  const fileName = String(
    result?.fileName ||
      result?.file_name ||
      doc?.filename ||
      doc?.file_name ||
      meta?.original_filename ||
      fallback?.fileName ||
      "Document"
  ).trim();

  const fileUrl =
    result?.fileUrl ||
    (Array.isArray(result?.fileUrls) ? result.fileUrls[0] : null) ||
    fallback?.fileUrl ||
    null;

  const fileUrls = Array.isArray(result?.fileUrls)
    ? result.fileUrls.filter(Boolean)
    : fileUrl
    ? [fileUrl]
    : Array.isArray(fallback?.fileUrls)
    ? fallback.fileUrls.filter(Boolean)
    : [];

  const publicProjection =
    result?.documentIntelligence &&
    typeof result.documentIntelligence === "object"
      ? result.documentIntelligence
      : result?.document_intelligence &&
        typeof result.document_intelligence === "object"
      ? result.document_intelligence
      : null;

  const projectionProgress =
    publicProjection?.progress &&
    typeof publicProjection.progress === "object"
      ? publicProjection.progress
      : {};

  const progressStage = String(
    doc?.progress_stage ||
      doc?.progressStage ||
      publicProjection?.progressStage ||
      projectionProgress?.stage ||
      fallback?.progressStage ||
      fallback?.progress_stage ||
      fallback?.documentStatus ||
      "processing"
  )
    .trim()
    .toLowerCase();

  const progressPercentRaw =
    doc?.progress_percent ??
    doc?.progressPercent ??
    publicProjection?.progressPercent ??
    projectionProgress?.percent ??
    fallback?.progressPercent ??
    fallback?.progress_percent ??
    null;

  const hasBackendProgressPercent =
    progressPercentRaw !== null &&
    progressPercentRaw !== undefined &&
    String(progressPercentRaw).trim() !== "";

  const parsedProgressPercent =
    hasBackendProgressPercent
      ? Number(progressPercentRaw)
      : Number.NaN;

  const progressPercent = Number.isFinite(parsedProgressPercent)
    ? Math.max(0, Math.min(100, parsedProgressPercent))
    : null;

  const progressMessage =
    String(
      doc?.progress_message ||
        doc?.progressMessage ||
        publicProjection?.progressMessage ||
        projectionProgress?.message ||
        fallback?.progressMessage ||
        fallback?.progress_message ||
        ""
    ).trim() || null;

  // Preserve Main's safe Document Intelligence projection all the way back to
  // ChatInterface. In particular, attachment-only sends need completionMessage
  // after the worker reaches ready/ready_with_warnings.
  //
  // The projection may arrive on the initial /commit response or, more commonly
  // for a longer-running document, on the terminal /status response. Falling
  // back to the previous projection also keeps it intact across polling passes.
  const documentIntelligence =
    publicProjection ||
    (fallback?.documentIntelligence &&
    typeof fallback.documentIntelligence === "object"
      ? fallback.documentIntelligence
      : fallback?.document_intelligence &&
        typeof fallback.document_intelligence === "object"
      ? fallback.document_intelligence
      : null);

  const rejectionCode =
    cleanErrorCode(doc?.rejection_code) ||
    cleanErrorCode(doc?.rejectionCode) ||
    cleanErrorCode(result?.rejection_code) ||
    cleanErrorCode(result?.rejectionCode) ||
    cleanErrorCode(fallback?.rejectionCode) ||
    null;

  const rejectionMessage =
    String(
      doc?.rejection_message ||
        doc?.rejectionMessage ||
        result?.rejection_message ||
        result?.rejectionMessage ||
        fallback?.rejectionMessage ||
        ""
    ).trim() || null;

  const failureCode =
    cleanErrorCode(doc?.failure_code) ||
    cleanErrorCode(doc?.failureCode) ||
    cleanErrorCode(result?.failure_code) ||
    cleanErrorCode(result?.failureCode) ||
    cleanErrorCode(fallback?.failureCode) ||
    null;

  const failureMessage =
    String(
      doc?.failure_message ||
        doc?.failureMessage ||
        result?.failure_message ||
        result?.failureMessage ||
        fallback?.failureMessage ||
        ""
    ).trim() || null;

  const responseCode =
    cleanErrorCode(result?.code) ||
    cleanErrorCode(dispatch?.code) ||
    cleanErrorCode(fallback?.responseCode) ||
    null;

  const terminalCode =
    rejectionCode ||
    failureCode ||
    responseCode ||
    cleanErrorCode(fallback?.terminalCode) ||
    null;

  const terminalMessage =
    rejectionMessage ||
    failureMessage ||
    String(
      result?.message ||
        result?.error ||
        dispatch?.message ||
        fallback?.terminalMessage ||
        ""
    ).trim() ||
    null;

  return {
    documentId,
    documentStatus,
    fileName: fileName || "Document",
    documentType: doc?.document_type || fallback?.documentType || null,
    documentTypeConfidence:
      doc?.document_type_confidence ??
      fallback?.documentTypeConfidence ??
      null,
    pageCount,
    workerRegion:
      doc?.worker_region ||
      dispatch?.region ||
      fallback?.workerRegion ||
      null,
    manifestPath: doc?.manifest_path || fallback?.manifestPath || null,
    briefPath: doc?.brief_path || fallback?.briefPath || null,
    pageMapPath: doc?.page_map_path || fallback?.pageMapPath || null,
    indexManifestPath:
      doc?.index_manifest_path ||
      fallback?.indexManifestPath ||
      null,
    warningCount:
      Number(doc?.warning_count ?? fallback?.warningCount ?? 0) || 0,
    rejectionCode,
    rejectionMessage,
    failureCode,
    failureMessage,
    responseCode,
    terminalCode,
    terminalMessage,
    progressStage,
    progressPercent,
    progressMessage,
    fileUrl,
    fileUrls,
    mode:
      result?.mode ||
      fallback?.mode ||
      "chat_document_intelligence",

    // Keep the public DI projection as a nested object. ChatInterface already
    // owns normalization and decides whether to use completionMessage for an
    // attachment-only turn or documentId/context for a typed user request.
    documentIntelligence,
    rawDocument: doc,
  };
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function pollFastChatDocumentStatus({
  base,
  idToken,
  documentId,
  fallbackMeta,
  onProgress,
  signal = null,
}) {
  let latest = { ...(fallbackMeta || {}) };

  for (
    let attempt = 0;
    attempt < FAST_CHAT_STATUS_POLL_MAX_ATTEMPTS;
    attempt += 1
  ) {
    if (signal?.aborted) {
      return latest;
    }

    const statusRes = await fetch(
      `${base}/chat/attachment/${encodeURIComponent(documentId)}/status`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
        ...(signal ? { signal } : null),
      }
    );

    const statusData = await readJsonResponse(
      statusRes,
      "Unable to read document processing status."
    );

    latest = documentMetaFromResult(statusData, latest);

    onProgress?.({
      stage:
        latest.progressStage ||
        latest.documentStatus ||
        "processing",

      // Never synthesize a percentage in the browser. The value below comes
      // from Main's document/status projection, whose source of truth is the
      // root Document Intelligence document record.
      progress: latest.progressPercent,
      progressStage:
        latest.progressStage ||
        latest.documentStatus ||
        "processing",
      progressPercent: latest.progressPercent,
      progressMessage:
        latest.progressMessage || null,
      ...latest,
    });

    if (FAST_CHAT_TERMINAL_STATUSES.has(latest.documentStatus)) {
      return latest;
    }

    await sleep(FAST_CHAT_STATUS_POLL_INTERVAL_MS);

    if (signal?.aborted) {
      return latest;
    }
  }

  throw createAttachmentError(
    "Document processing is still running after the maximum browser wait period.",
    {
      code: "document_status_poll_timeout",
      documentId,
      documentStatus: latest?.documentStatus || "processing",
    }
  );
}

async function commitAndPollFastChatDocument({
  base,
  idToken,
  sessionId,
  documentId,
  turnId = "",
  fileName = "Document",
  onProgress,
}) {
  if (!base) {
    throw createAttachmentError("Backend URL is not configured.", {
      code: "backend_url_not_configured",
    });
  }
  if (!idToken) {
    throw createAttachmentError("Missing or invalid token", {
      code: "authentication_required",
    });
  }
  if (!sessionId) {
    throw createAttachmentError("A Chat session is required before document processing.", {
      code: "attachment_session_required",
    });
  }
  if (!documentId) {
    throw createAttachmentError("A document ID is required before document processing.", {
      code: "attachment_document_id_required",
    });
  }

  const stableTurnId = String(turnId || "").trim();
  const safeFileName = String(fileName || "Document").trim() || "Document";

  onProgress?.({
    stage: "committing",
    progress: null,
    progressPercent: null,
    progressMessage: "Starting Document Intelligence.",
    documentId,
    fileName: safeFileName,
  });

  /*
   * Start /commit first, but DO NOT wait for it before observing progress.
   *
   * Long Document Intelligence work can continue for minutes while Main/worker
   * writes real progress_percent values to the root document record. Polling
   * /status concurrently lets those authoritative values reach ChatInterface
   * while the commit request is still in flight.
   *
   * This is shared by BOTH normal Chat uploads and File Vault -> Chat handoffs.
   */
  const commitRequest = fetch(`${base}/chat/attachment/commit`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({
      sessionId,
      ...(stableTurnId ? { turnId: stableTurnId } : {}),
      documentId,
    }),
  });

  const initialDocumentMeta = {
    documentId,
    documentStatus: "processing",
    fileName: safeFileName,
    progressStage: "committing",
    progressPercent: null,
    progressMessage: "Starting Document Intelligence.",
    mode: "chat_document_intelligence",
  };

  const statusPollController =
    typeof AbortController !== "undefined"
      ? new AbortController()
      : null;

  const statusPollingPromise = pollFastChatDocumentStatus({
    base,
    idToken,
    documentId,
    fallbackMeta: initialDocumentMeta,
    onProgress,
    signal: statusPollController?.signal || null,
  });

  let commit = null;
  let commitError = null;

  try {
    const commitRes = await commitRequest;
    commit = await readJsonResponse(
      commitRes,
      "Unable to commit the document upload."
    );
  } catch (error) {
    /*
     * A transport/HTTP failure from /commit is not, by itself, proof that the
     * document processing failed. The root /status record is authoritative.
     * Keep watching it; if it reaches a terminal state, use that result.
     */
    commitError = error;
  }

  let documentMeta = documentMetaFromResult(
    commit || {},
    initialDocumentMeta
  );

  if (FAST_CHAT_TERMINAL_STATUSES.has(documentMeta.documentStatus)) {
    try {
      statusPollController?.abort();
    } catch {}

    try {
      await statusPollingPromise;
    } catch (error) {
      if (error?.name !== "AbortError") {
        console.warn(
          "[AttachmentController] status polling stopped after terminal commit:",
          error?.message || error
        );
      }
    }
  } else {
    try {
      /*
       * If /commit returned quickly, this continues the same watcher.
       * If /commit is long-running, this watcher has already been delivering
       * real backend percentages through onProgress during the wait above.
       */
      documentMeta = await statusPollingPromise;
    } catch (statusError) {
      if (commitError) {
        throw commitError;
      }
      throw statusError;
    }
  }

  /*
   * If /commit had a transport failure but /status reached a real terminal
   * record, trust the terminal document state. Only surface the commit error
   * when no authoritative terminal status was observed.
   */
  if (
    commitError &&
    !FAST_CHAT_TERMINAL_STATUSES.has(documentMeta.documentStatus)
  ) {
    throw commitError;
  }

  if (!FAST_CHAT_SUCCESS_STATUSES.has(documentMeta.documentStatus)) {
    const doc = documentMeta.rawDocument || {};
    const terminalCode =
      documentMeta.rejectionCode ||
      documentMeta.failureCode ||
      documentMeta.terminalCode ||
      cleanErrorCode(doc?.rejection_code) ||
      cleanErrorCode(doc?.failure_code) ||
      "document_intelligence_terminal_failure";
    const terminalMessage =
      documentMeta.rejectionMessage ||
      documentMeta.failureMessage ||
      documentMeta.terminalMessage ||
      doc?.rejection_message ||
      doc?.failure_message ||
      `Document Intelligence ended with status "${
        documentMeta.documentStatus || "unknown"
      }".`;

    throw createAttachmentError(terminalMessage, {
      code: terminalCode,
      payload: {
        document: doc,
        code: terminalCode,
        message: terminalMessage,
      },
      documentId: documentMeta.documentId,
      documentStatus: documentMeta.documentStatus,
    });
  }

  onProgress?.({
    stage:
      documentMeta.progressStage ||
      documentMeta.documentStatus,
    progress: documentMeta.progressPercent,
    progressStage:
      documentMeta.progressStage ||
      documentMeta.documentStatus,
    progressPercent: documentMeta.progressPercent,
    progressMessage:
      documentMeta.progressMessage || null,
    ...documentMeta,
  });

  return documentMeta;
}

// Public continuation seam for File Vault -> Chat handoffs.
// The File Vault backend has already authenticated/authorized/copy-prepared the
// document and returned sessionId + documentId. From this point forward, use the
// exact same commit + authoritative status polling lifecycle as a normal Chat
// attachment. This helper performs no reserve and no browser upload.
export async function continueFastChatDocumentIntelligence({
  sessionId,
  documentId,
  turnId = "",
  fileName = "Document",
  onProgress,
} = {}) {
  const base = getBackendBase();
  if (!base) {
    throw createAttachmentError("Backend URL is not configured.", {
      code: "backend_url_not_configured",
    });
  }

  const idToken = await getFirebaseIdToken();

  return commitAndPollFastChatDocument({
    base,
    idToken,
    sessionId,
    documentId,
    turnId,
    fileName,
    onProgress,
  });
}

async function uploadFastChatOriginalFile({
  file,
  sessionId,
  turnId = "",
  onProgress,
}) {
  if (!file) {
    throw createAttachmentError("No document is selected.", {
      code: "attachment_no_file_selected",
    });
  }
  if (!sessionId) {
    throw createAttachmentError("A Chat session is required before upload.", {
      code: "attachment_session_required",
    });
  }

  if (file.size > FAST_CHAT_MAX_FILE_SIZE_MB * 1024 * 1024) {
    throw createAttachmentError(
      `File too large. Fast Chat supports files up to ${FAST_CHAT_MAX_FILE_SIZE_MB}MB.`,
      { code: "file_too_large" }
    );
  }

  if (!isAllowedFileType(file)) {
    throw createAttachmentError("Unsupported file type.", {
      code: "unsupported_file_type",
    });
  }

  const base = getBackendBase();
  if (!base) {
    throw createAttachmentError("Backend URL is not configured.", {
      code: "backend_url_not_configured",
    });
  }

  const idToken = await getFirebaseIdToken();
  const contentType = normalizedContentType(file);
  const stableTurnId = String(turnId || "").trim();

  onProgress?.({
    stage: "reserving",
    progress: null,
    progressPercent: null,
    progressMessage: "Preparing document upload.",
  });

  // /reserve is the authoritative current-plan/entitlement gate.
  // If it denies access, no signed PUT exists and no file reaches GCS.
  const reserveRes = await fetch(`${base}/chat/attachment/reserve`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({
      sessionId,
      ...(stableTurnId ? { turnId: stableTurnId } : {}),
      fileName: file.name,
      contentType,
      sizeBytes: file.size,
    }),
  });

  const reserve = await readJsonResponse(
    reserveRes,
    "Unable to reserve the document upload."
  );

  const documentId =
    reserve?.documentId ||
    reserve?.document_id ||
    null;
  const uploadUrl = reserve?.upload?.url || null;
  const uploadMethod = String(
    reserve?.upload?.method || "PUT"
  ).toUpperCase();
  const uploadHeaders =
    reserve?.upload?.headers &&
    typeof reserve.upload.headers === "object"
      ? reserve.upload.headers
      : { "Content-Type": contentType };

  if (!documentId || !uploadUrl || uploadMethod !== "PUT") {
    throw createAttachmentError("Document upload reservation was incomplete.", {
      code: "document_upload_reservation_incomplete",
      payload: reserve,
      documentId,
    });
  }

  onProgress?.({
    stage: "uploading",
    progress: null,
    progressPercent: null,
    progressMessage: "Uploading original document.",
    documentId,
  });

  // Send the exact ORIGINAL File bytes. No conversion or downscale.
  const putRes = await fetch(uploadUrl, {
    method: "PUT",
    headers: uploadHeaders,
    body: file,
  });

  if (!putRes.ok) {
    const putText = await putRes.text().catch(() => "");
    throw createAttachmentError(
      putText || `Direct document upload failed (${putRes.status}).`,
      {
        status: putRes.status,
        code: "direct_document_upload_failed",
        documentId,
      }
    );
  }

  return commitAndPollFastChatDocument({
    base,
    idToken,
    sessionId,
    documentId,
    turnId: stableTurnId,
    fileName: file.name,
    onProgress,
  });
}

export default function AttachmentController({
  fileInputRef,
  // (legacy compatibility only — backend ignores these)
  uid,
  email,
  userType,
  sessionKind,
  sessionId,
  turnId,
  onUploadingChange,
  onAttachmentReady,
}) {
  const [draft, setDraft] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dialogNotice, setDialogNotice] = useState(null);

  const showAttachmentDialog = (title, message) => {
    setDialogNotice({
      title: String(title || "Attachment notice"),
      message: String(message || "Please try again."),
    });
  };

  useEffect(() => {
    if (!dialogNotice) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setDialogNotice(null);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [dialogNotice]);

  useEffect(() => {
    onUploadingChange?.(uploading);
  }, [uploading, onUploadingChange]);

  const internalFileInputRef = useRef();
  const fileRef = fileInputRef || internalFileInputRef;

  // Vision Assist intentionally keeps this shared controller mounted in some
  // callers for compatibility, but manual attachments are disabled there.
  // Fast Chat / Document Intelligence behavior is unchanged.
  const visionAssistAttachmentsDisabled = isVisionAssistSession(sessionKind);

  useEffect(() => {
    return () => {
      if (draft?.previewUrl) {
        try {
          URL.revokeObjectURL(draft.previewUrl);
        } catch {}
      }
    };
  }, [draft]);

  const handleFileSelected = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;

    const resetInput = () => {
      try {
        if (e?.target) e.target.value = "";
      } catch {}
    };

    const safeKind = normalizeSessionKind(sessionKind);

    // =================================================================
    // VISION ASSIST — MANUAL ATTACHMENTS DISABLED
    // Keep the legacy implementation below dormant for future reuse, but do
    // not allow Vision Assist to enter any file/photo attachment pipeline.
    // Screen-share frames use a separate Vision Assist path and are untouched.
    // =================================================================
    if (isVisionAssistSession(safeKind)) {
      resetInput();
      return;
    }

    const fastChatMode = isFastChatSession(safeKind);

    // =================================================================
    // FAST CHAT / DOCUMENT INTELLIGENCE
    // Local selection ONLY. No backend call happens here.
    // =================================================================
    if (fastChatMode) {
      if (file.size > FAST_CHAT_MAX_FILE_SIZE_MB * 1024 * 1024) {
        showAttachmentDialog(
          "File too large",
          `Fast Chat supports files up to ${FAST_CHAT_MAX_FILE_SIZE_MB}MB.`
        );
        resetInput();
        return;
      }

      if (!isAllowedFileType(file)) {
        showAttachmentDialog(
          "Unsupported file type",
          "Please select a supported document or image file."
        );
        resetInput();
        return;
      }

      let previewUrl = null;

      try {
        // Preview generation is display-only. The authoritative source remains
        // the exact original File object selected by the user.
        previewUrl = await createSafePreview(file);

        setDraft({
          file,
          previewUrl,
          progress: 0,
          mode: "chat_document_intelligence_local",
        });

        onAttachmentReady?.({
          kind: "file",
          name: file.name,
          mime: file.type || normalizedContentType(file),
          fileExtension: fileExtensionFromName(file.name),
          fileTypeLabel: fileTypeLabel(
            file,
            file.type || normalizedContentType(file)
          ),
          sizeBytes: file.size,
          previewUrl,
          uploading: false,
          progress: 0,
          fileUrl: null,
          fileUrls: [],
          frameIds: [],
          frames: [],
          mode: "chat_document_intelligence_local",
          pendingUpload: true,

          // Local-only values. ChatInput will preserve these in its draft state.
          // uploadOnSend MUST be called only after the user presses Send.
          localFile: file,
          uploadOnSend: async ({
            sessionId: sendSessionId,
            turnId: sendTurnId,
            onProgress,
          } = {}) =>
            uploadFastChatOriginalFile({
              file,
              sessionId: sendSessionId || sessionId,
              // ChatInterface's per-Send value wins. The prop fallback keeps this
              // controller backward-compatible during the frontend rollout.
              turnId: sendTurnId || turnId || "",
              onProgress,
            }),
        });
      } catch (err) {
        console.error("Fast Chat attachment preparation failed:", err);

        if (previewUrl) {
          try {
            URL.revokeObjectURL(previewUrl);
          } catch {}
        }

        onAttachmentReady?.({
          kind: "file",
          name: file.name || "attachment",
          mime: file.type || "",
          fileExtension: fileExtensionFromName(file.name),
          fileTypeLabel: fileTypeLabel(file, file.type || ""),
          previewUrl: null,
          uploading: false,
          progress: 0,
          fileUrl: null,
          fileUrls: [],
          frameIds: [],
          frames: [],
          mode: "chat_document_intelligence_local",
          pendingUpload: false,
          error: String(err?.message || "Attachment preparation failed"),
          errorCode: cleanErrorCode(err?.code),
          errorStatus:
            Number.isFinite(Number(err?.status)) && Number(err?.status) > 0
              ? Number(err.status)
              : null,
          errorPayload:
            err?.payload && typeof err.payload === "object"
              ? err.payload
              : null,
        });

        showAttachmentDialog(
          "Attachment unavailable",
          err?.message || "Failed to prepare the attachment."
        );
      } finally {
        resetInput();
      }

      return;
    }

    // =================================================================
    // VISION ASSIST / LEGACY ATTACHMENT FLOW
    // Intentionally preserved for now. Vision still uploads immediately
    // through /attachment/upload. We will revisit Vision separately.
    // =================================================================

    if (file.size > VISION_LEGACY_MAX_FILE_SIZE_MB * 1024 * 1024) {
      showAttachmentDialog(
        "File too large",
        `The current Vision attachment flow supports files up to ${VISION_LEGACY_MAX_FILE_SIZE_MB}MB.`
      );
      resetInput();
      return;
    }

    if (!isAllowedFileType(file)) {
      showAttachmentDialog(
        "Unsupported file type",
        "Please select a supported document or image file."
      );
      resetInput();
      return;
    }

    let previewUrl = null;
    let finalFile = file;

    try {
      setUploading(true);
      onUploadingChange?.(true);

      // Legacy Vision behavior: downscale large images before upload.
      if (file.type?.startsWith("image/")) {
        const resized = await downscaleImage(file);
        if (resized) finalFile = resized;
      }

      // local preview (image only)
      previewUrl = await createSafePreview(finalFile);

      // store local draft (for cleanup)
      setDraft({ file: finalFile, previewUrl, progress: 5 });

      // notify parent immediately
      onAttachmentReady?.({
        kind: "file",
        name: finalFile.name,
        mime: finalFile.type || "",
        fileExtension: fileExtensionFromName(finalFile.name),
        fileTypeLabel: fileTypeLabel(finalFile, finalFile.type || ""),
        previewUrl,
        uploading: true,
        progress: 5,
        fileUrl: null,
        fileUrls: [],
        frameIds: [],
        frames: [],
      });

      // Vision legacy route still authenticates and uploads immediately.
      const idToken = await getFirebaseIdToken();

      const formData = new FormData();
      formData.append("file", finalFile);
      if (sessionId) formData.append("sessionId", sessionId);
      if (safeKind) formData.append("sessionKind", safeKind);

      const base = getBackendBase();
      const res = await fetch(`${base}/attachment/upload`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
        body: formData,
      });

      const text = await res.text();
      let result = null;

      try {
        result = JSON.parse(text);
      } catch {
        throw createAttachmentError(
          res.status === 401
            ? "Missing or invalid token"
            : "Upload failed: invalid server response",
          {
            status: res.status,
            code:
              res.status === 401
                ? "authentication_required"
                : "invalid_server_response",
          }
        );
      }

      if (!res.ok) {
        const msg =
          result?.message ||
          result?.error ||
          (res.status === 401
            ? "Missing or invalid token"
            : "Upload failed");
        throw createAttachmentError(msg, {
          status: res.status,
          code: extractBackendErrorCode(result),
          payload: result,
        });
      }

      const fileUrls = Array.isArray(result?.fileUrls)
        ? result.fileUrls
        : result?.fileUrl
        ? [result.fileUrl]
        : [];

      const primaryUrl = fileUrls[0] || null;

      const safeFrameIds = Array.isArray(result?.frame_ids)
        ? result.frame_ids
        : Array.isArray(result?.frameIds)
        ? result.frameIds
        : [];

      const frames = Array.isArray(result?.frames)
        ? result.frames
        : [];

      setDraft((prev) => ({
        ...(prev || {}),
        file: finalFile,
        previewUrl,
        progress: 100,
        fileUrl: primaryUrl,
        fileUrls,
      }));

      onAttachmentReady?.({
        kind: "file",
        name: finalFile.name,
        mime: finalFile.type || "",
        fileExtension: fileExtensionFromName(finalFile.name),
        fileTypeLabel: fileTypeLabel(finalFile, finalFile.type || ""),
        previewUrl,
        uploading: false,
        progress: 100,
        fileUrl: primaryUrl,
        fileUrls,
        frameIds: safeFrameIds,
        frames,
        mode: result?.mode || "",
        sessionDocPath: result?.session_doc_path || null,
      });
    } catch (err) {
      console.error("Upload or process failed:", err);

      onAttachmentReady?.({
        kind: "file",
        name: finalFile?.name || file?.name || "attachment",
        mime: finalFile?.type || file?.type || "",
        fileExtension: fileExtensionFromName(
          finalFile?.name || file?.name
        ),
        fileTypeLabel: fileTypeLabel(
          finalFile || file,
          finalFile?.type || file?.type || ""
        ),
        previewUrl: previewUrl || null,
        uploading: false,
        progress: 0,
        fileUrl: null,
        fileUrls: [],
        frameIds: [],
        frames: [],
        error: String(err?.message || "Upload failed"),
        errorCode: cleanErrorCode(err?.code),
        errorStatus:
          Number.isFinite(Number(err?.status)) && Number(err?.status) > 0
            ? Number(err.status)
            : null,
        errorPayload:
          err?.payload && typeof err.payload === "object"
            ? err.payload
            : null,
      });

      showAttachmentDialog(
        "Attachment unavailable",
        err?.message || "Failed to upload or process the file."
      );
    } finally {
      resetInput();
      setUploading(false);
      onUploadingChange?.(false);
    }
  };

  // Vision Assist must not expose a file input through this shared controller.
  // Returning null keeps the legacy implementation preserved in source while
  // making it unreachable from Vision Assist. This does not affect the
  // dedicated screen-share frame pipeline.
  if (visionAssistAttachmentsDisabled) {
    return null;
  }

  // Headless controller: provides the hidden file input plus a centered in-app
  // notice layer for local/legacy attachment errors. Fast Chat send-time errors
  // remain structured and are intentionally mapped by ChatInterface.
  return (
    <>
      <input
        type="file"
        style={{ display: "none" }}
        onChange={handleFileSelected}
        ref={fileRef}
        multiple={false}
        // default accept (menu will override per action)
        accept=".jpg,.jpeg,.png,.webp,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,image/*,application/pdf,text/plain,text/csv"
      />

      <AttachmentNoticeDialog
        notice={dialogNotice}
        onClose={() => setDialogNotice(null)}
      />
    </>
  );
}
